import { useAppSelector } from "@app/store";
import { Button } from "@components/button";
import { errorMessage } from "@components/forms/error";
import {
  cyrillicWithSpacesRegex,
  digitsRegex,
  errorCodeFieldRegex,
  phoneRegex,
  timeRegex,
} from "@components/forms/regexp";
import { sanitizePhoneNumber } from "@components/forms/utils";
import { Input } from "@components/input";
import { GlobalLoader } from "@components/loader";
import { Modal } from "@components/modal";
import { RadioButton } from "@components/radio-button";
import { CustomSelect, SelectOption } from "@components/select";
import { getBanks } from "@features/bank/api";
import { useDevicesDispatch } from "@features/device";
import { useRequisitesDispatch } from "@features/requisite";
import {
  CreateRequisiteForm as CreateRequisiteFormBase,
  RequisiteType,
} from "@features/requisite/api";
import { yupResolver } from "@hookform/resolvers/yup";
import { FetchClientError, JSONFetchClientError } from "@request/client";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import styles from "./add-detail-modal.module.scss";
import Checkbox from "@components/checkbox";
import { useAuth } from "@features/auth";
import { useNotification } from "@hooks/use-notification";
import ToggleSwitch from "@components/toggleOption";

type AddDetailModalProps = {
  isOpen: boolean;
  hide: () => void;
};

type CreateRequisiteForm = Omit<
  CreateRequisiteFormBase,
  "userId" | "currency"
> & { check: boolean; timeVal: string };

const schema = yup.object().shape({
  type: yup.string().required(errorMessage.required).trim(),
  account_number: yup.string().when("type", {
    is: (val: RequisiteType) => val === "ACCOUNT",
    then: (schema) =>
      schema
        .required(errorMessage.required)
        .matches(digitsRegex, errorMessage.onlyNumbers)
        .trim(),
    otherwise: (schema) => schema.notRequired(),
  }),
  bank_id: yup.string().required(errorMessage.required).trim(),
  card_number: yup
    .string()
    .required(errorMessage.required)
    .matches(digitsRegex, errorMessage.onlyNumbers)
    .trim()
    .min(16, (params) => errorMessage.min(params.min)),
  device_id: yup.string().when("$allowRequisitesWithoutDevice", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required(errorMessage.required).trim(),
  }),
  full_name: yup
    .string()
    .required(errorMessage.required)
    .min(2, (params) => errorMessage.min(params.min))
    .max(100, (params) => errorMessage.min(params.max))
    .matches(cyrillicWithSpacesRegex, errorMessage.onlyCyrillicWithSpaces)
    .trim()
    .when("$toggleVal", {
      is: "counterparties",
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required(errorMessage.required),
    }),
  phone_number: yup
    .string()
    .required(errorMessage.required)
    .transform(sanitizePhoneNumber)
    .matches(phoneRegex, errorMessage.invalidPhoneNumber)
    .trim()
    .when("$toggleVal", {
      is: "counterparties",
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required(errorMessage.required),
    }),
  start_time: yup
    .string()
    .notRequired()
    .trim()
    .when("$toggleVal", {
      is: "counterparties",
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema
          .required(errorMessage.required)
          .matches(timeRegex, errorMessage.time),
    }),
  end_time: yup
    .string()
    .notRequired()
    .trim()
    .when("$toggleVal", {
      is: "counterparties",
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema
          .required(errorMessage.required)
          .matches(timeRegex, errorMessage.time),
    }),
  max_transaction: yup
    .number()
    .transform((v, o) => (o === "" ? undefined : v))
    .notRequired()
    .positive(errorMessage.onlyPositive),
  min_transaction: yup
    .number()
    .transform((v, o) => (o === "" ? undefined : v))
    .notRequired()
    .positive(errorMessage.onlyPositive),
  max_amount_per_period: yup
    .number()
    .transform((v, o) => (o === "" ? undefined : v))
    .notRequired(),
  min_interval: yup
    .number()
    .transform((v, o) => (o === "" ? undefined : v))
    .notRequired()
    .positive(errorMessage.onlyPositive),
  transaction_count: yup
    .number()
    .transform((v, o) => (o === "" ? undefined : v))
    .notRequired()
    .positive(errorMessage.onlyPositive),
  intrabank_only: yup.boolean(),
});

export const AddDetailModal = ({ isOpen, hide }: AddDetailModalProps) => {
  const allowFarmCreation = useAppSelector(
    (state) => state.auth.allowFarmCreation
  );
  const allowRequisitesWithoutDevice = useAppSelector(
    (state) => state.auth.allowRequisitesWithoutDevice
  );
  const { isAdmin } = useAuth();
  const notif = useNotification();
  const [toggleVal, setToggleVal] = useState<
    "counterparties" | "transactionLimits"
  >("transactionLimits");

  const { createRequisite, getRequisites } = useRequisitesDispatch();

  const {
    control,
    handleSubmit,
    setError,
    reset,
    formState: { isSubmitting },
    watch,
    setValue,
  } = useForm<CreateRequisiteForm>({
    context: { allowRequisitesWithoutDevice, toggleVal },
    defaultValues: {
      timeVal: "sec",
    },
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    resolver: yupResolver(schema),
  });

  const isChecked = watch("check", false);
  useEffect(() => {
    if (isChecked) {
      // When checked, disable input and set predefined data
      setValue("start_time", "00:00");
      setValue("end_time", "23:59");
    } else {
      // When unchecked, enable input and clear it
      setValue("start_time", "");
      setValue("end_time", "");
    }
  }, [isChecked, setValue]);

  const onHide = () => {
    hide();
    reset();
  };

  const onSubmit = async (data: CreateRequisiteForm) => {
    try {
      if (toggleVal == "counterparties") {
        notif.addNotification("На данный момент нет контерагентов");
        return;
      }
      if (allowFarmCreation) {
        if (data.start_time == null) {
          data.start_time = "00:00";
        }
        if (data.end_time == null) {
          data.end_time = "23:59";
        }
        if (data.max_amount_per_period == null) {
          data.max_amount_per_period = 1000000;
        }
        if (data.max_transaction == null) {
          data.max_transaction = 300000;
        }
        if (data.min_transaction == null) {
          data.min_transaction = 100;
        }
        if (data.min_interval == null) {
          data.min_interval = 1;
        }
        if (data.transaction_count == null) {
          data.transaction_count = 500;
        }
        if (data.timeVal == "min" && data.min_interval) {
          data.min_interval = data.min_interval * 60;
        }
      }
      try {
        await createRequisite({ ...data, currency: "RUB" });
        onHide();
        getRequisites({ limit: 10, page: 1 });
      } catch (error) {
        if (error instanceof FetchClientError && error.data) {
          notif.addNotification(error.data);
        }
      }
    } catch (error) {
      if (error instanceof JSONFetchClientError) {
        const match = error.data?.code?.match(errorCodeFieldRegex);
        const field = match ? (match[1] as keyof CreateRequisiteForm) : null;

        if (field) {
          setError(field, { type: "server", message: error.data.error });
        }
      }
    }
  };

  const devicesSmall = useAppSelector((state) => state.device.devicesSmall);
  const { getDevicesSmall } = useDevicesDispatch();

  const [deviceOptions, setDeviceOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    getDevicesSmall();
  }, [getDevicesSmall]);

  useEffect(() => {
    const options = devicesSmall.map((deviceSmall) => ({
      text: deviceSmall.name,
      value: deviceSmall.id,
    }));

    setDeviceOptions(options);
  }, [devicesSmall]);

  const [bankOptions, setBankOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    getBanks().then((banks) => {
      const options = banks.map((bank) => ({
        text: bank.name,
        value: bank.id,
      }));

      setBankOptions(options);
    });
  }, []);

  const onToggle = (val: "counterparties" | "transactionLimits") => {
    setToggleVal(val);
  };

  return (
    <>
      <Modal
        className={styles.modal}
        isOpen={isOpen}
        onClose={onHide}
        title="Добавить новый реквизит"
      >
        <Input
          name="full_name"
          control={control}
          className={styles.input}
          placeholder="ФИО"
        />
        <Input
          name="phone_number"
          control={control}
          className={styles.input}
          mask="+7 (999) 999-99-99"
          placeholder="Номер телефона"
        />
        <Input
          name="card_number"
          control={control}
          className={styles.input}
          placeholder="Номер карты"
        />
        <CustomSelect
          classNames={{
            warpper: styles.selectWrapper,
            trigger: styles.input,
            error: styles.error,
          }}
          name="bank_id"
          control={control}
          options={bankOptions}
          placeholder="Банк"
        />
        <Input
          name="account_number"
          control={control}
          className={styles.input}
          placeholder="Счет"
        />
        <CustomSelect
          classNames={{
            warpper: styles.selectWrapper,
            trigger: styles.input,
            error: styles.error,
          }}
          name="device_id"
          control={control}
          options={deviceOptions}
          placeholder="Устройство"
        />
        {(!!allowFarmCreation || isAdmin) && (
          <div className={styles.twoInputs}>
            <p className={styles.title}>Суммы сделок</p>
            <div className={styles.inputs}>
              <Input
                name="min_transaction"
                placeholder="Минимальная"
                type="number"
                postNote="руб"
                className={styles.input}
                control={control}
              />
              <Input
                name="max_transaction"
                placeholder="Максимальная"
                type="number"
                postNote="руб"
                className={styles.input}
                control={control}
              />
            </div>
          </div>
        )}
        <ToggleSwitch onToggle={onToggle} defaultValue={toggleVal} />
        {(!!allowFarmCreation || isAdmin) &&
          toggleVal == "transactionLimits" && (
            <>
              <div className={styles.twoInputs}>
                <p className={styles.title}>
                  Время работы реквизита <small>(UTC+3 МСК)</small>
                </p>
                <div className={styles.inputs}>
                  <Input
                    control={control}
                    name="start_time"
                    className={styles.input}
                    mask="99:99"
                    placeholder="Начало"
                  />
                  <Input
                    control={control}
                    name="end_time"
                    className={styles.input}
                    mask="99:99"
                    placeholder="Конец"
                  />
                </div>
                <div style={{ display: "inline-block", marginTop: "10px" }}>
                  <Checkbox
                    control={control}
                    name="check"
                    label="Круглосуточно"
                  />
                </div>
              </div>
              <Input
                name="transaction_count"
                placeholder="Количество сделок за период"
                type="number"
                postNote="шт"
                control={control}
                className={styles.input}
              />
              <Input
                control={control}
                name="max_amount_per_period"
                type="number"
                placeholder="Максимальная сумма за период"
                postNote="руб"
                className={styles.input}
              />
              <Checkbox
                control={control}
                name="intrabank_only"
                label="Внутрибанковский режим (IBT)"
                style={{ marginBottom: "10px" }}
              />
              <div className={styles.timeInterval}>
                <label className={styles.label}>
                  Минимальный интервал между сделками
                </label>
                <div className={styles.flexdiv}>
                  <div style={{ flexGrow: 1 }}>
                    <Input
                      control={control}
                      name="min_interval"
                      className={styles.input}
                    />
                  </div>
                  <CustomSelect
                    name="timeVal"
                    control={control}
                    options={timeOptions}
                  />
                </div>
              </div>
            </>
          )}
        {toggleVal == "counterparties" && (
          // <div>
          <Input
            name="test"
            control={control}
            className={styles.input}
            placeholder="Количество контрагентов"
          />
          //</div>
        )}
        <RadioButton
          name="type"
          control={control}
          className={styles.input}
          options={[
            { label: "СБП", value: "SBP" },
            { label: "Номер карты", value: "CARD" },
            { label: "Номер счета", value: "ACCOUNT" },
          ]}
        />
        {!!allowFarmCreation && (
          <div style={{ width: "310px", fontSize: "14px" }}>
            Если не указаны лимиты, реквизит будет работать в стандартном режиме
          </div>
        )}
        <div className={styles.buttons}>
          <Button type="outline" onClick={onHide}>
            Отмена
          </Button>
          <Button onClick={handleSubmit(onSubmit)}>Добавить</Button>
        </div>
      </Modal>
      {isSubmitting && <GlobalLoader />}
    </>
  );
};

const timeOptions = [
  { text: "мин", value: "min" },
  { text: "сек", value: "sec" },
];
